/**
 * useMedicalNotes — buffer y temporizador del pipeline de notas médicas.
 *
 * Rediseño NU (documento único): este hook ya NO mantiene un SOAP/FHIR local
 * ni fusiona resultados — el servidor aplica cada ciclo directamente sobre
 * `consultas/{id}/notas_medicas/actual` (merge canónico con guardas) y la
 * pantalla lo recibe por `useLiveSoap` (onSnapshot del documento).
 *
 * Responsabilidades que conserva:
 *   - Acumular los segmentos de transcripción en un buffer local.
 *   - Disparar `portal_procesarTranscripcion` cada `intervalSeconds` cuando el
 *     buffer supera `minCharsToProcess` (el contexto vivo lo arma el SERVIDOR;
 *     aquí solo viaja el bloque estático del paciente).
 *   - flushPending() antes de colgar (ignora el umbral de caracteres).
 *   - Restaurar el buffer cuando la IA no está disponible (success:false).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../config/firebase';

export interface TranscriptEntry {
  id: number;
  speaker: string;
  text: string;
  isFinal: boolean;
}

export interface PatientContext {
  name?: string;
  age?: number;
  gender?: string;
  allergies?: string[];
  medications?: string[];
  conditions?: string[];
}

interface UseMedicalNotesOptions {
  patient: PatientContext;
  motivoConsulta: string;
  consultaId: string;
  /** Interval in seconds between AI processing cycles (default 30) */
  intervalSeconds?: number;
  /** Minimum new characters before triggering processing (default 80) */
  minCharsToProcess?: number;
  enabled?: boolean;
}

interface CFResponse {
  success: boolean;
  version?: number;
  error?: string;
}

interface MedicalNotesState {
  isProcessing: boolean;
  lastProcessedAt: Date | null;
  processCount: number;
  error: string | null;
}

export function useMedicalNotes(options: UseMedicalNotesOptions) {
  const {
    patient,
    motivoConsulta,
    consultaId,
    intervalSeconds = 30,
    minCharsToProcess = 80,
    enabled = true,
  } = options;

  const [state, setState] = useState<MedicalNotesState>({
    isProcessing: false,
    lastProcessedAt: null,
    processCount: 0,
    error: null,
  });

  const bufferRef = useRef<string>('');
  const lastProcessedIndexRef = useRef<number>(0);
  const processingRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setInterval>>(undefined);

  const patientRef = useRef(patient);
  patientRef.current = patient;

  const motivoRef = useRef(motivoConsulta);
  motivoRef.current = motivoConsulta;

  const consultaIdRef = useRef(consultaId);
  consultaIdRef.current = consultaId;

  /**
   * Contexto ESTÁTICO del paciente para la CF. La NOTA ACTUAL, los RECURSOS
   * ACTUALES (con ids) y los eliminados los construye el SERVIDOR leyendo el
   * documento único — el navegador ya no es fuente de ese estado.
   */
  const buildContexto = useCallback((): string => {
    const p = patientRef.current;
    const parts: string[] = [];
    if (p.name) parts.push(`Paciente: ${p.name}`);
    if (p.age) parts.push(`Edad: ${p.age} años`);
    if (p.gender) parts.push(`Género: ${p.gender}`);
    if (p.allergies?.length) parts.push(`Alergias: ${p.allergies.join(', ')}`);
    if (p.medications?.length) parts.push(`Medicamentos: ${p.medications.join(', ')}`);
    if (p.conditions?.length) parts.push(`Condiciones: ${p.conditions.join(', ')}`);
    if (motivoRef.current) parts.push(`Motivo: ${motivoRef.current}`);
    return parts.join('. ');
  }, []);

  /** Call the Cloud Function */
  const callProcessarCF = useCallback(
    async (transcripcion: string): Promise<CFResponse | null> => {
      if (!consultaIdRef.current) return null;
      const fn = httpsCallable<
        { consultaId: string; transcripcion: string; contexto?: string },
        CFResponse
      >(functions, 'portal_procesarTranscripcion');
      const result = await fn({
        consultaId: consultaIdRef.current,
        transcripcion,
        contexto: buildContexto(),
      });
      return result.data;
    },
    [buildContexto],
  );

  /** Add new transcript segments to the buffer */
  const addTranscriptSegments = useCallback((segments: TranscriptEntry[]) => {
    const newText = segments
      .filter((s) => s.isFinal && s.id > lastProcessedIndexRef.current)
      .map((s) => `[${s.speaker}]: ${s.text}`)
      .join('\n');

    if (newText) {
      bufferRef.current += (bufferRef.current ? '\n' : '') + newText;
      const maxId = Math.max(...segments.filter((s) => s.isFinal).map((s) => s.id));
      if (maxId > lastProcessedIndexRef.current) {
        lastProcessedIndexRef.current = maxId;
      }
    }
  }, []);

  /**
   * Procesa un texto dado. Si la IA no está disponible (success:false) o la
   * llamada falla, restaura el tramo al buffer para reintentarlo.
   */
  const procesarTexto = useCallback(
    async (textToProcess: string) => {
      processingRef.current = true;
      setState((prev) => ({ ...prev, isProcessing: true, error: null }));
      try {
        const data = await callProcessarCF(textToProcess);
        if (data && data.success === false) {
          bufferRef.current = textToProcess + (bufferRef.current ? '\n' + bufferRef.current : '');
          setState((prev) => ({
            ...prev,
            isProcessing: false,
            error: 'La IA no está disponible; se reintentará automáticamente',
          }));
          return;
        }
        setState((prev) => ({
          ...prev,
          isProcessing: false,
          lastProcessedAt: new Date(),
          processCount: prev.processCount + 1,
        }));
      } catch (err) {
        bufferRef.current = textToProcess + (bufferRef.current ? '\n' + bufferRef.current : '');
        setState((prev) => ({
          ...prev,
          isProcessing: false,
          error: err instanceof Error ? err.message : 'Error de procesamiento',
        }));
      } finally {
        processingRef.current = false;
      }
    },
    [callProcessarCF],
  );

  /** Process the motivo de consulta as initial input */
  const processMotivo = useCallback(async () => {
    if (!motivoConsulta.trim() || !consultaIdRef.current || processingRef.current) return;
    await procesarTexto(`Motivo de consulta del paciente: ${motivoConsulta}`);
  }, [motivoConsulta, procesarTexto]);

  /** Process accumulated buffer through CF (respeta el umbral de caracteres) */
  const processBuffer = useCallback(async () => {
    if (!bufferRef.current.trim() || !consultaIdRef.current || processingRef.current) return;
    if (bufferRef.current.length < minCharsToProcess) return;
    const textToProcess = bufferRef.current;
    bufferRef.current = '';
    await procesarTexto(textToProcess);
  }, [minCharsToProcess, procesarTexto]);

  /** Force process now (manual trigger — ignora el umbral) */
  const processNow = useCallback(async () => {
    if (!bufferRef.current.trim() || processingRef.current) return;
    const textToProcess = bufferRef.current;
    bufferRef.current = '';
    await procesarTexto(textToProcess);
  }, [procesarTexto]);

  /**
   * flushPending — procesa TODO lo que quede pendiente y resuelve solo cuando
   * termina. Se usa antes de colgar/finalizar para que el último tramo de la
   * conversación entre en la nota (ignora el umbral de caracteres). Espera a
   * que cualquier ciclo en curso termine y vacía el buffer restante.
   */
  const flushPending = useCallback(async (): Promise<void> => {
    const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));
    const deadline = Date.now() + 30000; // techo de seguridad (30 s)

    // 1) Esperar a que termine el ciclo periódico que pudiera estar corriendo.
    while (processingRef.current && Date.now() < deadline) await sleep(150);

    // 2) Vaciar el buffer restante (processNow ignora el umbral de 80 chars).
    //    Reintentar por si llegaron segmentos nuevos mientras se procesaba.
    let intentos = 0;
    while (bufferRef.current.trim() && Date.now() < deadline && intentos < 6) {
      await processNow();
      intentos++;
      await sleep(80);
      while (processingRef.current && Date.now() < deadline) await sleep(150);
    }
  }, [processNow]);

  /** ¿Hay trabajo de IA pendiente AHORA? (lectura viva de refs, no del estado). */
  const hasPendingWork = useCallback(
    () => processingRef.current || bufferRef.current.trim().length > 0,
    [],
  );

  // Periodic processing timer
  useEffect(() => {
    if (!enabled || !consultaId) return;
    timerRef.current = setInterval(() => {
      processBuffer();
    }, intervalSeconds * 1000);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [enabled, consultaId, intervalSeconds, processBuffer]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  return {
    ...state,
    addTranscriptSegments,
    processMotivo,
    processNow,
    flushPending,
    hasPendingWork,
    bufferLength: bufferRef.current.length,
  };
}
